export type ConversionCategory =
  | 'email'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'image'
  | 'html'
  | 'code'
  | 'text'
  | 'unsupported';

export interface DetectedFile {
  category: ConversionCategory;
  /** Uppercase extension without dot, e.g. DOCX */
  extension: string;
  mimeType: string;
}

const EXT_MAP: Record<string, ConversionCategory> = {
  // Email
  eml: 'email',
  msg: 'email',
  // Documents
  docx: 'document',
  doc: 'document',
  txt: 'text',
  rtf: 'document',
  md: 'text',
  markdown: 'text',
  // HTML treated as own pipeline for settings
  html: 'html',
  htm: 'html',
  // Spreadsheets
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  // Presentations
  pptx: 'presentation',
  ppt: 'presentation',
  // Images
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  gif: 'image',
  bmp: 'image',
  tiff: 'image',
  tif: 'image',
  heic: 'image',
  heif: 'image',
  svg: 'image',
  // Code / data
  json: 'code',
  xml: 'code',
  yaml: 'code',
  yml: 'code',
  js: 'code',
  jsx: 'code',
  ts: 'code',
  tsx: 'code',
  mjs: 'code',
  cjs: 'code',
  py: 'code',
  sql: 'code',
  css: 'code',
  scss: 'code',
  less: 'code',
};

function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  if (i === -1) return '';
  return name.slice(i + 1).toUpperCase();
}

export function detectFileCategory(file: File): DetectedFile {
  const ext = extensionOf(file.name);
  const lower = ext.toLowerCase();
  const mimeType = file.type || 'application/octet-stream';

  if (!lower) {
    return { category: 'unsupported', extension: '', mimeType };
  }

  const cat = EXT_MAP[lower];
  if (!cat) {
    return { category: 'unsupported', extension: ext, mimeType };
  }

  if (lower === 'doc') {
    return { category: 'unsupported', extension: ext, mimeType };
  }

  if (lower === 'ppt' && cat === 'presentation') {
    return { category: 'unsupported', extension: ext, mimeType };
  }

  return { category: cat, extension: ext, mimeType };
}

export function humanCategoryLabel(category: ConversionCategory): string {
  switch (category) {
    case 'email':
      return 'Email';
    case 'document':
      return 'Document';
    case 'spreadsheet':
      return 'Spreadsheet';
    case 'presentation':
      return 'Presentation';
    case 'image':
      return 'Image';
    case 'html':
      return 'HTML';
    case 'code':
      return 'Code';
    case 'text':
      return 'Text';
    default:
      return 'Unsupported';
  }
}
